import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth as authApi, setAccessToken } from '../services/api.js'
import type { User } from '@tykkets/shared'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const initialised = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isLoggedIn = computed(() => !!user.value)

  async function init() {
    if (initialised.value) return
    try {
      // Try to restore session via refresh cookie
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ''}/api/auth/refresh`,
        { method: 'POST', credentials: 'include' }
      )
      const data = await res.json()
      if (data.success) {
        setAccessToken(data.data.accessToken)
        await fetchMe()
      } else {
        // Refresh failed — log reason for debugging
      }
    } catch (e) {
    } finally {
      initialised.value = true
    }
  }

  async function fetchMe() {
    const res = await authApi.me()
    if (res.success) user.value = res.data
  }

  async function requestMagicLink(email: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const res = await authApi.requestMagicLink(email)
      if (!res.success) throw new Error(res.error.message)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to send magic link'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function verifyToken(token: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const res = await authApi.verify(token)
      if (!res.success) throw new Error(res.error.message)
      setAccessToken(res.data.accessToken)
      user.value = res.data.user
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Verification failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    // Clear local state immediately before anything async
    setAccessToken(null)
    user.value        = null
    initialised.value = false
    try { sessionStorage.clear(); localStorage.removeItem('_tk') } catch {}

    // Clear all stores explicitly — Android WebView keeps heap alive between navigations
    try {
      const { useTicketStore } = await import('./tickets.js')
      useTicketStore().clear()
    } catch {}

    // Tell backend to invalidate the refresh token (best effort)
    try { await authApi.logout() } catch {}

    // Hard redirect to login
    window.location.replace('/welcome')
  }


  return {
    user, initialised, loading, error,
    isLoggedIn,
    init, fetchMe, requestMagicLink, verifyToken, logout,
  }
})
